/**
 * Client-side error reporter -- captures unhandled errors and sends them
 * to the backend in batches for server-side analysis.
 *
 * Runs as a persistent background feature. Captures:
 * - window.onerror (uncaught exceptions)
 * - unhandledrejection (unhandled promise rejections)
 * - Manual reportError() calls from catch blocks
 *
 * Buffers errors and flushes every 30 seconds or when the batch hits 10 errors.
 */

const FLUSH_INTERVAL = 30_000; // 30 seconds
const BATCH_LIMIT = 10;
const ENDPOINT = '/api/log/client-errors';

interface ErrorEntry {
  message: string;
  stack?: string;
  url?: string;
  timestamp: string;
  extra?: Record<string, unknown>;
}

// Patterns that match common token/credential forms in string values.
// Applied case-insensitively.
const SCRUB_PATTERNS: Array<[RegExp, string]> = [
  // Bearer tokens in Authorization headers or log strings
  [/Bearer\s+\S+/gi, 'Bearer [REDACTED]'],
  // URL/query-string params carrying tokens or secrets
  [
    /(token|access_token|refresh_token|id_token|code|password|secret|api[_-]?key|authorization)=([^&\s"']+)/gi,
    '$1=[REDACTED]',
  ],
  // URL PATH-segment tokens: reset / verify / magic-link style routes carry the
  // secret in the path, not the query string (e.g. /reset-password/<token>).
  // Redact the segment immediately following a known credential-bearing keyword.
  [
    /(\/(?:reset(?:-password)?|verify(?:-email)?|magic-?link|confirm|activate|invite|set-password)\/)([^/?#\s"']+)/gi,
    '$1[REDACTED]',
  ],
];

/**
 * Scrub tokens and PII from a string.
 * Exported for unit testing.
 * Never throws -- on any internal error the original string is returned
 * so callers can decide to drop it.
 */
export function scrubSensitive(value: string): string {
  try {
    let result = value;
    for (const [pattern, replacement] of SCRUB_PATTERNS) {
      result = result.replace(pattern, replacement);
    }
    return result;
  } catch {
    // Return empty string on scrub failure -- fail closed on secret leak.
    return '';
  }
}

// Bound the recursion so a circular reference or a pathologically deep/large
// payload cannot stack-overflow or explode the upload size. A cycle is replaced
// with the marker rather than walked again; past the depth cap we stop and emit
// the marker (the subtree is not leaked — only truncated).
const MAX_SCRUB_DEPTH = 8;
const CIRCULAR_MARKER = '[Circular]';
const TRUNCATED_MARKER = '[Truncated]';

/**
 * Recursively scrub every string value reachable within a value.
 * Strings are scrubbed; arrays and plain objects are walked depth-first;
 * everything else passes through unchanged. A token nested inside a structured
 * payload (e.g. { response: { headers: { authorization: 'Bearer ...' } } })
 * must not survive into the upload.
 *
 * `seen` tracks the current ancestor chain to break circular references;
 * `depth` caps total recursion. Never throws -- on any internal failure that
 * subtree is dropped (fail closed).
 *
 * NOTE: non-plain object values (Map, Set, Error, class instances) are walked
 * via Object.entries, so they flatten to their own-enumerable props (typically
 * `{}`). This is intentionally fail-safe (any string contents are dropped, not
 * forwarded) but means such values lose their debugging shape. The built-in
 * window handlers only ever pass plain `{line,col}` / `{type}` objects.
 */
function scrubDeep(val: unknown, depth = 0, seen?: WeakSet<object>): unknown {
  try {
    if (typeof val === 'string') {
      return scrubSensitive(val);
    }
    if (val === null || typeof val !== 'object') {
      return val;
    }
    if (depth >= MAX_SCRUB_DEPTH) {
      return TRUNCATED_MARKER;
    }
    const ancestors = seen ?? new WeakSet<object>();
    if (ancestors.has(val as object)) {
      return CIRCULAR_MARKER;
    }
    ancestors.add(val as object);
    let out: unknown;
    if (Array.isArray(val)) {
      out = val.map((v) => scrubDeep(v, depth + 1, ancestors));
    } else {
      const obj: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(val as Record<string, unknown>)) {
        obj[k] = scrubDeep(v, depth + 1, ancestors);
      }
      out = obj;
    }
    // Pop this node off the ancestor chain so sibling subtrees that legitimately
    // reference the same object aren't falsely flagged circular.
    ancestors.delete(val as object);
    return out;
  } catch {
    // Fail closed: drop this subtree rather than risk leaking it.
    return undefined;
  }
}

/**
 * Scrub all string values within an extra Record, recursively.
 * Returns a new object; drops any key whose scrub throws.
 * Never throws overall.
 */
function scrubExtra(extra: Record<string, unknown>): Record<string, unknown> {
  try {
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(extra)) {
      try {
        result[key] = scrubDeep(val);
      } catch {
        // Drop the field on scrub failure -- fail closed.
      }
    }
    return result;
  } catch {
    // If the whole loop fails, drop extra entirely.
    return {};
  }
}

let buffer: ErrorEntry[] = [];

function addError(entry: ErrorEntry) {
  // Scrub sensitive data from all string fields before buffering.
  // This is the single choke point for both window handlers and reportError.
  let message = entry.message;
  let stack = entry.stack;
  let url = entry.url;
  let extra = entry.extra;

  try {
    message = scrubSensitive(entry.message);
  } catch {
    message = '';
  }

  if (stack !== undefined) {
    try {
      stack = scrubSensitive(stack);
    } catch {
      // Drop stack on failure -- fail closed.
      stack = undefined;
    }
  }

  if (url !== undefined) {
    try {
      // A URL/filename can carry a token in its query string (e.g. an OAuth
      // callback or a tokened script URL in a stack-derived filename).
      url = scrubSensitive(url);
    } catch {
      // Drop url on failure -- fail closed.
      url = undefined;
    }
  }

  if (extra !== undefined) {
    extra = scrubExtra(extra);
  }

  buffer.push({ ...entry, message, stack, url, extra });
  if (buffer.length >= BATCH_LIMIT) {
    flush();
  }
}

async function flush() {
  if (buffer.length === 0) return;

  const batch = buffer.splice(0);
  const token = localStorage.getItem('responsekit_token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-RK-Version': __APP_VERSION__,
    'X-RK-Client': window.matchMedia('(display-mode: standalone)').matches ? 'pwa' : 'browser',
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  try {
    await fetch(ENDPOINT, {
      method: 'POST',
      headers,
      body: JSON.stringify({ errors: batch }),
    });
  } catch {
    // If flush fails, put errors back (but cap to prevent memory leak)
    buffer.unshift(...batch.slice(0, 50 - buffer.length));
  }
}

/**
 * Report an error manually from a catch block.
 */
export function reportError(message: string, extra?: Record<string, unknown>) {
  addError({
    message,
    url: window.location.pathname,
    timestamp: new Date().toISOString(),
    extra,
  });
}

/**
 * Initialize the global error reporter. Call once from main.tsx.
 */
export function initErrorReporter() {
  // Capture uncaught exceptions
  window.addEventListener('error', (event) => {
    addError({
      message: event.message || String(event.error),
      stack: event.error?.stack,
      url: event.filename || window.location.pathname,
      timestamp: new Date().toISOString(),
      extra: { line: event.lineno, col: event.colno },
    });
  });

  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    addError({
      message: reason instanceof Error ? reason.message : String(reason),
      stack: reason instanceof Error ? reason.stack : undefined,
      url: window.location.pathname,
      timestamp: new Date().toISOString(),
      extra: { type: 'unhandledrejection' },
    });
  });

  // Periodic flush
  setInterval(flush, FLUSH_INTERVAL);

  // Flush on page unload
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flush();
    }
  });
}
